// UpgradeDashboard.jsx
// pages/Dashboard/UpgradeDashboard.jsx

import React, { useState, useEffect, useCallback, useRef } from "react";
import {
  RefreshCw,
  Clock,
  AlertTriangle,
  CheckCircle,
  Calendar,
  Download,
  ChevronDown,
  ChevronRight,
  X,
  ArrowUpCircle,
  Users,
  Wrench,
  TrendingUp,
} from "lucide-react";
import apiService from "../../services/api";
import { useTheme } from "../../contexts/ThemeContext";

// ============================================
// UTILITY FUNCTIONS
// ============================================

const formatDate = (dateString) => {
  if (!dateString) return "—";
  return new Date(dateString).toLocaleDateString("en-GB", {
    day: "numeric",
    month: "short",
    year: "numeric",
  });
};

const formatMonthYear = (dateString) => {
  if (!dateString) return "—";
  return new Date(dateString)
    .toLocaleDateString("en-GB", { month: "long", year: "numeric" })
    .toUpperCase();
};

const getDaysUntilDue = (dateString) => {
  if (!dateString) return null;
  const due = new Date(dateString);
  const now = new Date();
  const diff = Math.ceil((due - now) / (1000 * 60 * 60 * 24));
  return diff;
};

// ============================================
// STAT CARD COMPONENT
// ============================================

const StatCard = ({ title, value, subtitle, icon: Icon, color = "amber" }) => {
  const { isDarkMode } = useTheme();
  const colorConfig = {
    amber: {
      bg: "from-amber-500/10 to-amber-600/5",
      border: "border-amber-500/20",
      text: "text-amber-400",
      icon: "text-amber-400",
    },
    red: {
      bg: "from-red-500/10 to-red-600/5",
      border: "border-red-500/20",
      text: "text-red-400",
      icon: "text-red-400",
    },
    green: {
      bg: "from-green-500/10 to-green-600/5",
      border: "border-green-500/20",
      text: "text-green-400",
      icon: "text-green-400",
    },
    blue: {
      bg: "from-blue-500/10 to-blue-600/5",
      border: "border-blue-500/20",
      text: "text-blue-400",
      icon: "text-blue-400",
    },
    purple: {
      bg: "from-purple-500/10 to-purple-600/5",
      border: "border-purple-500/20",
      text: "text-purple-400",
      icon: "text-purple-400",
    },
  };

  const colors = colorConfig[color] || colorConfig.amber;

  return (
    <div
      className={`bg-gradient-to-br ${colors.bg} border ${colors.border} rounded-xl p-4 hover:scale-105 transition-transform duration-200`}
    >
      <div className="flex items-start justify-between mb-3">
        <span
          className={`text-xs font-medium uppercase tracking-wide ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
        >
          {title}
        </span>
        <Icon size={16} className={colors.icon} />
      </div>
      <div
        className={`text-2xl font-bold ${isDarkMode ? "text-white" : "text-gray-900"}`}
      >
        {value}
      </div>
      {subtitle && (
        <div className={`text-sm ${colors.text} mt-1 font-medium`}>
          {subtitle}
        </div>
      )}
    </div>
  );
};

// ============================================
// UPGRADE ROW COMPONENT
// ============================================

const UpgradeRow = ({ record, isOverdue }) => {
  const { isDarkMode } = useTheme();
  const daysUntil = getDaysUntilDue(record["Upgrade Due"]);

  const getUrgencyBadge = () => {
    if (isOverdue || (daysUntil !== null && daysUntil < 0)) {
      return (
        <span className="px-2 py-0.5 rounded text-xs font-semibold bg-red-500/15 text-red-400 border border-red-500/25">
          Overdue
        </span>
      );
    }
    if (daysUntil !== null && daysUntil <= 14) {
      return (
        <span className="px-2 py-0.5 rounded text-xs font-semibold bg-amber-500/15 text-amber-400 border border-amber-500/25">
          {daysUntil}d left
        </span>
      );
    }
    return (
      <span className="px-2 py-0.5 rounded text-xs font-semibold bg-blue-500/15 text-blue-400 border border-blue-500/25">
        Due This Month
      </span>
    );
  };

  return (
    <tr
      className={`border-b transition-colors ${isDarkMode ? "border-gray-800/60 hover:bg-gray-800/20" : "border-gray-200 hover:bg-gray-50"}`}
    >
      <td className="py-3 px-4">
        <span
          className={`font-medium text-sm ${isDarkMode ? "text-white" : "text-gray-900"}`}
        >
          {record["Opportunity"] || record.name || "—"}
        </span>
      </td>
      <td
        className={`py-3 px-4 text-sm ${isDarkMode ? "text-gray-300" : "text-gray-600"}`}
      >
        {record["Account"] || "—"}
      </td>
      <td
        className={`py-3 px-4 text-sm ${isDarkMode ? "text-gray-300" : "text-gray-600"}`}
      >
        {record["Assigned To"] || "—"}
      </td>
      <td
        className={`py-3 px-4 text-sm ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
      >
        {formatDate(record["Install Date"])}
      </td>
      <td className="py-3 px-4">
        <span
          className={`text-sm font-semibold ${
            isOverdue || (daysUntil !== null && daysUntil < 0)
              ? "text-red-400"
              : "text-amber-400"
          }`}
        >
          {formatMonthYear(record["Upgrade Due"])}
        </span>
      </td>
      <td className="py-3 px-4">{getUrgencyBadge()}</td>
    </tr>
  );
};

// ============================================
// UPGRADE TABLE COMPONENT
// ============================================

const UpgradeTable = ({
  title,
  data,
  color,
  emptyMessage,
  defaultExpanded = true,
}) => {
  const { isDarkMode } = useTheme();
  const [expanded, setExpanded] = useState(defaultExpanded);

  if (!data) {
    return (
      <div
        className={`rounded-xl p-6 text-center text-sm ${isDarkMode ? "bg-[#0C0E12] border border-gray-800 text-gray-500" : "bg-white border border-gray-200 text-gray-400"}`}
      >
        Loading...
      </div>
    );
  }

  const isOverdue = title.toLowerCase().includes("overdue");
  const overdueRecords = data.filter(
    (r) =>
      r["Status"] === "Overdue" || (r["Status"] === undefined && isOverdue),
  );
  const thisMonthRecords = data.filter((r) => r["Status"] === "Due This Month");

  const colorBorder = {
    red: "border-red-500/30",
    amber: "border-amber-500/30",
    blue: "border-blue-500/30",
  };
  const colorTitle = {
    red: "text-red-400",
    amber: "text-amber-400",
    blue: "text-blue-400",
  };

  return (
    <div
      className={`rounded-xl overflow-hidden ${isDarkMode ? "bg-[#0C0E12] border" : "bg-white border"} ${colorBorder[color] || "border-gray-800"}`}
    >
      {/* Card Header */}
      <button
        onClick={() => setExpanded(!expanded)}
        className={`w-full flex items-center justify-between p-4 transition-colors ${isDarkMode ? "hover:bg-gray-800/20" : "hover:bg-gray-50"}`}
      >
        <div className="flex items-center gap-3">
          <span
            className={`text-sm font-semibold ${colorTitle[color] || (isDarkMode ? "text-white" : "text-gray-900")}`}
          >
            {title}
          </span>
          <span
            className={`text-xs px-2 py-0.5 rounded-full ${isDarkMode ? "bg-gray-800 text-gray-300" : "bg-gray-100 text-gray-600"}`}
          >
            {data.length} record{data.length !== 1 ? "s" : ""}
          </span>
        </div>
        {expanded ? (
          <ChevronDown
            size={16}
            className={isDarkMode ? "text-gray-400" : "text-gray-500"}
          />
        ) : (
          <ChevronRight
            size={16}
            className={isDarkMode ? "text-gray-400" : "text-gray-500"}
          />
        )}
      </button>

      {expanded && (
        <>
          {data.length === 0 ? (
            <div
              className={`px-4 pb-6 text-center text-sm ${isDarkMode ? "text-gray-500" : "text-gray-400"}`}
            >
              {emptyMessage}
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className={isDarkMode ? "bg-gray-900/60" : "bg-gray-50"}>
                  <tr
                    className={`border-b ${isDarkMode ? "border-gray-800" : "border-gray-200"}`}
                  >
                    <th
                      className={`text-left py-3 px-4 font-medium text-xs uppercase tracking-wide ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
                    >
                      Opportunity
                    </th>
                    <th
                      className={`text-left py-3 px-4 font-medium text-xs uppercase tracking-wide ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
                    >
                      Account
                    </th>
                    <th
                      className={`text-left py-3 px-4 font-medium text-xs uppercase tracking-wide ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
                    >
                      Assigned To
                    </th>
                    <th
                      className={`text-left py-3 px-4 font-medium text-xs uppercase tracking-wide ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
                    >
                      Install Date
                    </th>
                    <th
                      className={`text-left py-3 px-4 font-medium text-xs uppercase tracking-wide ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
                    >
                      Upgrade Due
                    </th>
                    <th
                      className={`text-left py-3 px-4 font-medium text-xs uppercase tracking-wide ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
                    >
                      Status
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {data.map((record, idx) => (
                    <UpgradeRow
                      key={record.id || idx}
                      record={record}
                      isOverdue={record["Status"] === "Overdue"}
                    />
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}
    </div>
  );
};

// ============================================
// MAIN UPGRADE DASHBOARD COMPONENT
// ============================================

const UpgradeDashboard = () => {
  const { isDarkMode } = useTheme();
  const [upgradeReports, setUpgradeReports] = useState({});
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [lastRefresh, setLastRefresh] = useState(null);
  const [error, setError] = useState(null);

  const refreshIntervalRef = useRef(null);

  const UPGRADE_REPORTS = [
    "upgrades_due_this_month",
    "upgrades_due_outstanding",
  ];

  const loadDashboardData = useCallback(
    async (showRefreshing = true, forceRefresh = false) => {
      try {
        if (showRefreshing) setRefreshing(true);
        setError(null);

        // Fetch all upgrade-type reports
        const reportsResponse = await apiService.getReports({
          isPublic: true,
          reportType: "upgrades",
          limit: 50,
        });

        const upgradeData = {};

        for (const report of reportsResponse.data) {
          if (UPGRADE_REPORTS.includes(report.developerName)) {
            try {
              const result = await apiService.runReport(
                report.id,
                {},
                forceRefresh,
              );
              upgradeData[report.developerName] = {
                ...report,
                results: result,
              };
            } catch (err) {
              console.error(`Failed to run report ${report.name}:`, err);
            }
          }
        }

        setUpgradeReports(upgradeData);
        setLastRefresh(new Date());
      } catch (err) {
        console.error("Failed to load upgrade dashboard data:", err);
        setError("Failed to load upgrade data. Please try again.");
      } finally {
        setLoading(false);
        setRefreshing(false);
      }
    },
    [],
  );

  // Initial load
  useEffect(() => {
    loadDashboardData(false, false);
  }, [loadDashboardData]);

  // Hourly auto-refresh
  useEffect(() => {
    refreshIntervalRef.current = setInterval(() => {
      loadDashboardData(true, false);
    }, 3600000);
    return () => {
      if (refreshIntervalRef.current) {
        clearInterval(refreshIntervalRef.current);
      }
    };
  }, [loadDashboardData]);

  // Export handler
  const handleExport = async (developerName, format = "csv") => {
    try {
      const report = upgradeReports[developerName];
      if (!report) return;
      const blob = await apiService.exportReport(report.id, format);
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${developerName}_${new Date().toISOString().split("T")[0]}.${format}`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (err) {
      console.error("Export failed:", err);
    }
  };

  // Derive data from report results
  const thisMonthData =
    upgradeReports.upgrades_due_this_month?.results?.data || null;
  const outstandingData =
    upgradeReports.upgrades_due_outstanding?.results?.data || null;

  // Split outstanding into overdue vs this month
  const overdueRecords = outstandingData
    ? outstandingData.filter((r) => r["Status"] === "Overdue")
    : null;
  const thisMonthFromOutstanding = outstandingData
    ? outstandingData.filter((r) => r["Status"] === "Due This Month")
    : null;

  // Stats
  const totalDueThisMonth = thisMonthData?.length || 0;
  const totalOverdue = overdueRecords?.length || 0;
  const totalOutstanding = outstandingData?.length || 0;

  // Group overdue by assigned person for the breakdown
  const overdueByPerson = overdueRecords
    ? overdueRecords.reduce((acc, r) => {
        const name = r["Assigned To"] || "Unassigned";
        acc[name] = (acc[name] || 0) + 1;
        return acc;
      }, {})
    : {};

  const topOverdue = Object.entries(overdueByPerson)
    .sort((a, b) => b[1] - a[1])
    .slice(0, 5);

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-96">
        <div className="text-center">
          <RefreshCw
            size={48}
            className="text-amber-500 animate-spin mx-auto mb-4"
          />
          <p className={isDarkMode ? "text-gray-400" : "text-gray-500"}>
            Loading upgrade dashboard...
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6 p-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1
            className={`text-2xl font-bold flex items-center gap-2 ${isDarkMode ? "text-white" : "text-gray-900"}`}
          >
            <ArrowUpCircle size={24} className="text-amber-400" />
            Upgrade Dashboard
          </h1>
          <p
            className={`text-sm mt-1 ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
          >
            Track customer upgrades due 18 months after installation
          </p>
        </div>
        <div className="flex items-center gap-3">
          {lastRefresh && (
            <span
              className={`text-xs flex items-center gap-1 ${isDarkMode ? "text-gray-500" : "text-gray-400"}`}
            >
              <Clock size={12} />
              Updated {lastRefresh.toLocaleTimeString()}
            </span>
          )}
          <button
            onClick={() => handleExport("upgrades_due_outstanding")}
            disabled={!outstandingData}
            className={`flex items-center gap-2 px-3 py-2 border rounded-lg text-sm transition-colors disabled:opacity-40 ${isDarkMode ? "bg-gray-800 hover:bg-gray-700 border-gray-700 text-white" : "bg-gray-100 hover:bg-gray-200 border-gray-300 text-gray-700"}`}
          >
            <Download size={14} />
            Export Outstanding
          </button>
          <button
            onClick={() => loadDashboardData(true, true)}
            disabled={refreshing}
            className="flex items-center gap-2 px-4 py-2 bg-amber-600 hover:bg-amber-700 disabled:bg-amber-800 rounded-lg text-white text-sm transition-colors"
          >
            <RefreshCw size={16} className={refreshing ? "animate-spin" : ""} />
            {refreshing ? "Refreshing..." : "Refresh"}
          </button>
        </div>
      </div>

      {/* Error */}
      {error && (
        <div className="bg-red-500/10 border border-red-500/20 rounded-lg p-4 text-red-400 text-sm">
          {error}
        </div>
      )}

      {/* No reports seeded yet */}
      {!loading &&
        totalDueThisMonth === 0 &&
        totalOutstanding === 0 &&
        !error && (
          <div className="bg-amber-500/5 border border-amber-500/20 rounded-xl p-6 text-center">
            <ArrowUpCircle size={40} className="text-amber-400 mx-auto mb-3" />
            <p
              className={`font-semibold mb-1 ${isDarkMode ? "text-white" : "text-gray-900"}`}
            >
              No upgrade reports found
            </p>
            <p
              className={`text-sm ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
            >
              Go to <strong>Reports</strong> and click{" "}
              <strong>"Create Upgrade Reports"</strong> to seed the two upgrade
              report templates, then come back here.
            </p>
          </div>
        )}

      {/* Stats Grid */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <StatCard
          title="Due This Month"
          value={totalDueThisMonth}
          icon={Calendar}
          color="amber"
          subtitle="Pending upgrades"
        />
        <StatCard
          title="Overdue"
          value={totalOverdue}
          icon={AlertTriangle}
          color="red"
          subtitle="Past due date"
        />
        <StatCard
          title="Total Outstanding"
          value={totalOutstanding}
          icon={TrendingUp}
          color="blue"
          subtitle="All incomplete"
        />
        <StatCard
          title="Assigned To"
          value={Object.keys(overdueByPerson).length}
          icon={Users}
          color="purple"
          subtitle="People with overdue"
        />
      </div>

      {/* Overdue breakdown by person */}
      {topOverdue.length > 0 && (
        <div
          className={`rounded-xl p-5 ${isDarkMode ? "bg-[#0C0E12] border border-red-500/20" : "bg-white border border-red-200"}`}
        >
          <h3 className="text-sm font-semibold text-red-400 mb-4 flex items-center gap-2">
            <AlertTriangle size={14} />
            Overdue Upgrades by Assigned Person
          </h3>
          <div className="space-y-2">
            {topOverdue.map(([name, count]) => {
              const pct = Math.round((count / totalOverdue) * 100);
              return (
                <div key={name} className="flex items-center gap-3">
                  <span
                    className={`text-sm w-36 truncate ${isDarkMode ? "text-gray-300" : "text-gray-600"}`}
                  >
                    {name}
                  </span>
                  <div
                    className={`flex-1 rounded-full h-2 overflow-hidden ${isDarkMode ? "bg-gray-800" : "bg-gray-200"}`}
                  >
                    <div
                      className="h-2 rounded-full bg-red-500/70 transition-all duration-500"
                      style={{ width: `${pct}%` }}
                    />
                  </div>
                  <span
                    className={`text-sm font-semibold w-6 text-right ${isDarkMode ? "text-white" : "text-gray-900"}`}
                  >
                    {count}
                  </span>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* This Month Table */}
      <div>
        <div className="flex items-center justify-between mb-3">
          <h2
            className={`text-base font-semibold flex items-center gap-2 ${isDarkMode ? "text-white" : "text-gray-900"}`}
          >
            <Calendar size={16} className="text-amber-400" />
            Upgrades Due This Month
          </h2>
          <button
            onClick={() => handleExport("upgrades_due_this_month")}
            disabled={!thisMonthData || thisMonthData.length === 0}
            className={`flex items-center gap-1.5 px-3 py-1.5 border rounded-lg text-xs transition-colors disabled:opacity-40 ${isDarkMode ? "bg-gray-800 hover:bg-gray-700 border-gray-700 text-gray-300" : "bg-gray-100 hover:bg-gray-200 border-gray-300 text-gray-600"}`}
          >
            <Download size={12} />
            Export CSV
          </button>
        </div>
        <UpgradeTable
          title={`Due This Month (${totalDueThisMonth})`}
          data={thisMonthData || []}
          color="amber"
          emptyMessage="No upgrades due this month 🎉"
          defaultExpanded={true}
        />
      </div>

      {/* Outstanding / Overdue Table */}
      <div>
        <div className="flex items-center justify-between mb-3">
          <h2
            className={`text-base font-semibold flex items-center gap-2 ${isDarkMode ? "text-white" : "text-gray-900"}`}
          >
            <AlertTriangle size={16} className="text-red-400" />
            All Outstanding Upgrades
          </h2>
          <button
            onClick={() => handleExport("upgrades_due_outstanding")}
            disabled={!outstandingData || outstandingData.length === 0}
            className={`flex items-center gap-1.5 px-3 py-1.5 border rounded-lg text-xs transition-colors disabled:opacity-40 ${isDarkMode ? "bg-gray-800 hover:bg-gray-700 border-gray-700 text-gray-300" : "bg-gray-100 hover:bg-gray-200 border-gray-300 text-gray-600"}`}
          >
            <Download size={12} />
            Export CSV
          </button>
        </div>

        {/* Overdue section */}
        {overdueRecords !== null && overdueRecords.length > 0 && (
          <div className="mb-4">
            <UpgradeTable
              title={`Overdue (${overdueRecords.length})`}
              data={overdueRecords}
              color="red"
              emptyMessage="No overdue upgrades"
              defaultExpanded={true}
            />
          </div>
        )}

        {/* Due this month within outstanding */}
        {thisMonthFromOutstanding !== null &&
          thisMonthFromOutstanding.length > 0 && (
            <UpgradeTable
              title={`Due This Month — Not Yet Done (${thisMonthFromOutstanding.length})`}
              data={thisMonthFromOutstanding}
              color="amber"
              emptyMessage="None"
              defaultExpanded={false}
            />
          )}

        {outstandingData && outstandingData.length === 0 && (
          <div className="bg-green-500/5 border border-green-500/20 rounded-xl p-8 text-center">
            <CheckCircle size={40} className="text-green-400 mx-auto mb-3" />
            <p
              className={`font-semibold ${isDarkMode ? "text-white" : "text-gray-900"}`}
            >
              All caught up!
            </p>
            <p
              className={`text-sm mt-1 ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
            >
              No outstanding upgrades at this time.
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default UpgradeDashboard;
